// Components
import { Button, Menu, MenuDivider, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from "@fluentui/react-components";
import { DismissSmall, IconSize, More } from "@ms/planner-generic-controls/lib/local/icons";
import { SecureLink } from "@ms/planner-generic-controls/lib/local/secureLink/SecureLink";
// Constants
import { LearnMoreLinks } from "@ms/uno-constants/lib/local/AppConstants";
import { ContextDisplayType } from "@ms/planner-models/lib/local/service/graph/general/ContextDisplayType";
import { ContextState } from "@ms/planner-models/lib/local/service/graph/general/ContextState";
import { getSyncContextDisplayText } from "@ms/planner-models/lib/local/utilities/ContextLinkUtilities";
// Styles
import { usePlanContextDropdownStyles } from "./PlanContextDropdown.styles";
// Utilities
import { format } from "@fluentui/utilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import * as React from "react";
import { getContextItemIcon } from "../../utilities/IconUtilities";
import { useLoggers } from "../../utilities/hooks/useLoggers";
const ComponentName = "PlanContextDropdown";
/**
 * Plan Context Dropdown control that displays the context details of a plan
 */ export const PlanContextDropdown = ({ title, staticMenuItems, delinkSection, visibleContextLinks, containerDisplayName })=>{
    const loggers = useLoggers();
    const [isOpen, setIsOpen] = React.useState(false);
    const styles = usePlanContextDropdownStyles();
    const contextCommandButton = React.useRef(null);
    /** Handles open/close of the dropdown
     * @param state Whether the dropdown is open
     */ const onOpenClose = (state)=>{
        if (state === true) {
            loggers.engagementLogger.logEngagement(ComponentName, "PlanContextDropdownClicked");
        }
        setIsOpen(state);
    };
    /**
     * Handle when a context item is clicked
     * @param webUrl The web URL of the context that was clicked
     * @param displayAs The display type of the context that was clicked
     */ const handleContextClick = (webUrl, displayAs)=>{
        safelyOpenExternalUrl(webUrl, loggers.traceLogger);
        const additionalLoggingInfo = {};
        if (displayAs != null) {
            additionalLoggingInfo["displayAs"] = displayAs;
        }
        loggers.engagementLogger.logEngagement(ComponentName, "PlanContextClicked", additionalLoggingInfo);
    };
    /**
     * Creates a menu item for a given plan context link
     * @param contextLink The plan context link to create a menu item for
     */ const getMenuItemForContentLink = (contextLink)=>{
        const displayAs = contextLink.displayAs;
        if (!displayAs) {
            return null;
        }
        const linkText = getSyncContextDisplayText(contextLink, containerDisplayName);
        const text = title;
        // Dictionary mapping display types to their configuration
        const displayTypeConfig = {
            [ContextDisplayType.MeetingNotes]: {
                text: Strings.PlanContextLinking_MeetingNotes,
                ariaLabel: format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, Strings.PlanContextLinking_MeetingNotes, Strings.PlanContextLinking_MicrosoftLoop)
            },
            [ContextDisplayType.LoopPage]: {
                text,
                ariaLabel: format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, linkText, Strings.PlanContextLinking_MicrosoftLoop)
            },
            [ContextDisplayType.SharePointPage]: {
                text,
                ariaLabel: format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, linkText, Strings.PlanContextLinking_SharePoint)
            },
            [ContextDisplayType.TeamsTab]: {
                text,
                ariaLabel: format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, linkText, Strings.PlanContextLinking_Teams)
            },
            [ContextDisplayType.Other]: {
                text: contextLink.customLinkText ?? linkText,
                ariaLabel: format(Strings.PlanContextLinking_ExternalContextLinkAriaLabel, contextLink.customLinkText ?? linkText, Strings.PlanContextLinking_Other)
            }
        };
        const config = displayTypeConfig[displayAs];
        const secondaryText = contextLink.isActive === false ? Strings.PlanContextLinking_DelinkedContextSecondaryText : linkText;
        const status = contextLink.isActive ? ContextState.Active : ContextState.Delinked;
        const icon = getContextItemIcon(displayAs, "", IconSize.Large);
        const element = contextLink.webUrl !== "" ? /*#__PURE__*/ React.createElement(MenuItem, {
            key: contextLink.id,
            onClick: ()=>handleContextClick(contextLink.webUrl, displayAs),
            icon: {
                children: icon,
                className: styles.contextItemIcon
            },
            className: styles.contextItem,
            "aria-label": config.ariaLabel,
            content: {
                className: styles.contextItemContent
            },
            "data-testid": "plan-context-linked-item",
            subText: /*#__PURE__*/ React.createElement("div", {
                className: styles.contextDetailsSecondaryText
            }, status === ContextState.Delinked && /*#__PURE__*/ React.createElement(DismissSmall, {
                className: styles.linkDismiss
            }), secondaryText)
        }, /*#__PURE__*/ React.createElement("span", {
            className: styles.contextDetailsText
        }, config.text)) : null;
        return element;
    };
    /**
     * Sets up the context button props
     */ const getDropdownItems = ()=>{
        if (!staticMenuItems && visibleContextLinks.length < 1) {
            // Empty context case
            return null;
        }
        const dropdownItems = [];
        if (visibleContextLinks.length > 0) {
            // Header element always exists in the dropdown
            dropdownItems.push(/*#__PURE__*/ React.createElement(MenuItem, {
                key: "header",
                className: styles.headerItem,
                "aria-label": Strings.PlanContextLinking_MultipleLinkedContextsAriaLabel,
                "data-testid": "plan-context-header-menu-item"
            }, /*#__PURE__*/ React.createElement("span", {
                className: styles.headerText
            }, Strings.PlanContextLinking_MultipleLinkedContextsText, " ", /*#__PURE__*/ React.createElement(SecureLink, {
                className: styles.headerLink,
                href: LearnMoreLinks.linkedPlans
            }, Strings.SharedComponents_LearnMoreLink))));
            // If there is more than one context then add a divider between header and context items
            if (visibleContextLinks.length > 1) {
                dropdownItems.push(/*#__PURE__*/ React.createElement(MenuDivider, {
                    key: "divider_1",
                    className: styles.menuBorder
                }));
            }
            // Fill in the contextual item from the content links
            for (const contentLink of visibleContextLinks){
                const element = getMenuItemForContentLink(contentLink);
                if (element) {
                    dropdownItems.push(element);
                }
            }
            // If there are any context menu item callbacks, add a divider between channel link items and context menu items
            if (staticMenuItems && staticMenuItems.length > 0) {
                dropdownItems.push(/*#__PURE__*/ React.createElement(MenuDivider, {
                    key: "divider_2",
                    className: styles.menuBorder
                }));
            }
        }
        if (staticMenuItems && staticMenuItems.length > 0) {
            staticMenuItems.forEach((staticLink)=>{
                dropdownItems.push(/*#__PURE__*/ React.createElement(MenuItem, {
                    key: staticLink.key,
                    onClick: ()=>staticLink.onClick(contextCommandButton.current),
                    icon: {
                        children: staticLink.icon
                    },
                    "aria-label": staticLink.text,
                    content: {
                        className: styles.staticItemContent
                    },
                    disabled: staticLink.disabled,
                    title: staticLink.title,
                    "data-testid": staticLink.dataTestId
                }, /*#__PURE__*/ React.createElement("span", {
                    className: styles.staticText
                }, staticLink.text)));
            });
        }
        return dropdownItems;
    };
    const dropdownItems = getDropdownItems();
    if (!dropdownItems) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: styles.root
    }, /*#__PURE__*/ React.createElement(Menu, {
        open: isOpen,
        onOpenChange: (e, data)=>onOpenClose(data.open),
        positioning: {
            offset: 4
        }
    }, /*#__PURE__*/ React.createElement(MenuTrigger, {
        disableButtonEnhancement: true
    }, /*#__PURE__*/ React.createElement(Tooltip, {
        content: Strings.PlanHeader_MoreOptionsTooltip,
        relationship: "label"
    }, /*#__PURE__*/ React.createElement(Button, {
        ref: contextCommandButton,
        className: styles.customMenuButton,
        onClick: ()=>onOpenClose(!isOpen),
        "aria-label": format(Strings.PlanHeader_PlanHeaderOptionsAriaLabel, title),
        "aria-expanded": isOpen,
        "aria-haspopup": true,
        appearance: "transparent",
        icon: /*#__PURE__*/ React.createElement(More, null),
        size: "small",
        "data-testid": "plan-header-more-options-button"
    }))), /*#__PURE__*/ React.createElement(MenuPopover, {
        className: styles.menuRoot,
        "data-testid": "plan-header-more-options-menu"
    }, /*#__PURE__*/ React.createElement(MenuList, null, dropdownItems))), /*#__PURE__*/ React.createElement("div", {
        className: styles.linkedBadgeItem
    }, delinkSection));
};
